import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { TextField  } from '@mui/material';
import { yupResolver } from "@hookform/resolvers/yup";
import Select, { components } from "react-select";
import * as Yup from "yup";

import ModalWrapperChildren from '@/views/components/Modal/ModalWrapperChildren';
import api from "@/services/api";
import { authActions } from '@/store/actions/auth.actions';
import SimpleAlert from '@/views/components/Alert/Alert';
import LettersDeptAutoSuggest from "@/views/components/AutoSuggestInput/LettersDeptAutoSuggest";

import TrashIcon from '@/assets/icons/trash-icon.svg';
import PlusIcon from '@/assets/icons/plus-icon.svg';

const ModalAddUser = ({reloadList, itemToEdit, setItemToEdit, showModalAddDepartment, setShowModalAddDepartment}) => {
	const { dict, usersLimit, mode } = useSelector(state => state.app);
	const [ requestIsPending, setRequestIsPending ] = useState(false);
	const { user } = useSelector((state) => state.auth);
	const dispatch = useDispatch();

	const roles =  [
		{ value: "reader", label:  dict.roles.reader },
		{ value: "editor", label: dict.roles.editor },
		{ value: "expert", label: dict.roles.expert },
		{ value: "admin", label: dict.roles.admin },
	];

	const defaultValues = {
		forename: '',
		surname: '',
		email: '',
		role: mode === 'standalone' ? roles[1] : '',
		departments: [ { name: '' } ],
	};

	const tFieldRequired = dict.users.validation.fieldRequired;

	const validationSchema = Yup.object({
		forename: Yup.string().required(tFieldRequired).trim(),
		surname: Yup.string().required(tFieldRequired).trim(),
		email: Yup.string().email(dict.users.validation.email).required(tFieldRequired),
		role: Yup.object()
			.shape({value: Yup.string().required(tFieldRequired), label: Yup.string()})
			.required(tFieldRequired)
			.nullable(),
	});

	const checkDepartments = (itemToEdit) => {
		if (itemToEdit?.departments?.length === 0) return {...itemToEdit, departments: [ { name: "" } ]};
		return {...itemToEdit, departments: [ ...itemToEdit.departments ].map(({name, id}) => ({name, departmentId: id}))};
	};

	const { handleSubmit, control, setError, watch, formState: { errors, isValid }, setValue, reset } = useForm({
		resolver: yupResolver(validationSchema),
		defaultValues: itemToEdit ? checkDepartments(itemToEdit) : defaultValues,
		reValidateMode: 'onChange',
		mode: 'onChange',
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'departments',
	});

	const watchFirstDepartmentsField = watch('departments[0].name');

	const handleClose = () => {
		if (setItemToEdit) setItemToEdit(null);
		setShowModalAddDepartment(false);
	};

	const setCustomErrors = (err) => {
		for (let i = 0; i < err.length; ++i) {
			let errorCode = err[i].code;
			if(errorCode === 'field.limitExceeded') {
				setError('role', { type: "custom", message: "Limit użytkowników dla wybranej roli został już osiągnięty" });
			}

			if (errorCode === 'field.notUnique') {
				setError('email', { type: "custom", message: "Konto z podanym adresem email już istnieje" });
			}
		}
	};

	const getErrors = (data) => {
		if(data.errors) {
			const error = data.errors.map(err => err);
			setError('name', error[0]);
		}
	};

	useEffect(() => {
		if (itemToEdit?.role) {
			const receivedValue =  itemToEdit.role;
			const matchedOption = roles.find(option => option.value === receivedValue);

			setTimeout(() => {
				setValue("role", matchedOption, { shouldValidate: true });
			}, 10);
		}
		// eslint-disable-next-line
	}, [itemToEdit]);

	const submit = async (data) => {
		setRequestIsPending(true);

		const {surname, forename, email, role, departments} = data;

		const filteredDepartments = departments.filter(({name}) => name?.trim() !== '');
		const uniqueDepartments = Array.from(
			new Map(filteredDepartments.map(item => [ item.name, item ])).values(),
		);

		if (mode === 'standalone') {
			if(uniqueDepartments.length === 0 && role.value !== 'admin') {
				setError('role', { type: "custom", message: "Konto, którego rolą nie jest administrator, musi mieć podaną organizację" });
				setRequestIsPending(false);
				return;
			}
		}

		const dataToSend = {
			surname,
			forename,
			email,
			departments: uniqueDepartments,
			role: role.value,
		};

		if(itemToEdit) {
			try {
				const res = await api.put(`/users/${itemToEdit.id}`, dataToSend);
				if(res.status === 200) {
					if(user.id === res.data.id) dispatch(authActions.setUser(res.data));
					setShowModalAddDepartment(false);
					setRequestIsPending(false);
					setItemToEdit(null);
					reloadList();
				} else {
					setCustomErrors(res.data.errors);
					getErrors(res.data);
					setRequestIsPending(false);
				}
			} catch (err) {
				setCustomErrors(err.response.data.errors);
				getErrors(err.response.data.errors);
				setRequestIsPending(false);
			}

		} else {
			try {
				const res = await api.post(`/users`, dataToSend);

				if(res.status === 200) {
					setShowModalAddDepartment(false);
					setRequestIsPending(false);
					reloadList();
					setItemToEdit(null);
				} else {
					setCustomErrors(res.data.errors);
					getErrors(res.data);
					setRequestIsPending(false);
				}
			} catch (err) {
				setCustomErrors(err.response.data.errors);
				getErrors(err.response.data);
				setRequestIsPending(false);
			}

		}

	};

	return (
		<ModalWrapperChildren
			onClose={ handleClose }
			setModalVisible={ setShowModalAddDepartment }
			modalVisible={ showModalAddDepartment }
			onConfirm={ handleSubmit(submit) }
			closeButtonName={ dict.cancel }
			confirmButtonName={ itemToEdit ? dict.users.modalAdd.editSaveBtn :dict.add }
			disabled={ !isValid }
			requestIsPending={ requestIsPending }
			title={ itemToEdit ? dict.users.modalEdit.title : dict.users.modalAdd.title }
			description={ <> 
				{itemToEdit ? <>
					<span>{dict.users.modalEdit.desc} <strong>{itemToEdit.displayName}</strong></span>
				</> : <>
					{dict.users.modalAdd.desc}
				</> }
				{usersLimit && (usersLimit.admins || usersLimit.experts || usersLimit.editors || usersLimit.readers) && <SimpleAlert variant="warning"
					className = "modal__load-dictionary--warning"
					style={ { marginTop: "15px" } }
					alertContent = { <> 
						<span className="modal__load-dictionary--error-message-header">{ dict.users.modalLoad.limitWarning.part1 }</span>
						{ dict.users.modalLoad.limitWarning.part2 } 
						{ usersLimit.admins } 
						{ usersLimit.admins === 1 ? dict.users.modalLoad.limitWarning.part3Singular : dict.users.modalLoad.limitWarning.part3 }
						{ usersLimit.experts } 
						{ usersLimit.experts === 1 ? dict.users.modalLoad.limitWarning.part4Singular : dict.users.modalLoad.limitWarning.part4 } 
						{ usersLimit.editors } 
						{ usersLimit.editors === 1 ? dict.users.modalLoad.limitWarning.part5Singular : dict.users.modalLoad.limitWarning.part5 } 
						{ usersLimit.readers } 
						{ usersLimit.readers === 1 ? dict.users.modalLoad.limitWarning.part6Singular : dict.users.modalLoad.limitWarning.part6 }
					</> } /> }
			</> }
			requiredFields
			isLine
		>
			<form>
				<label
					className="modal-children__label"
					htmlFor='forename'>
					{dict.users.modalAdd.name}
				</label>
				<Controller
					render={ ({ field }) => <TextField
						size='small'
						fullWidth
						placeholder={ dict.users.modalAdd.placeholderName }
						inputProps={ { maxLength: 256 } }
						{ ...field }
					/> }
					name='forename'
					control={ control }
					errors={ errors.forename }
				/>
				<p className="modal-children__error">{errors && errors.forename && errors.forename.message}</p>
				<label
					className="modal-children__label"
					htmlFor='surname'>
					{dict.users.modalAdd.surname}
				</label>
				<Controller
					render={ ({ field }) => <TextField
						size='small'
						fullWidth
						placeholder={ dict.users.modalAdd.placeholderSurname }
						inputProps={ { maxLength: 256 } }
						{ ...field }
					/> }
					name='surname'
					control={ control }
					errors={ errors.surname }
				/>
				<p className="modal-children__error">{errors && errors.surname && errors.surname.message}</p>
				<label
					className="modal-children__label"
					htmlFor='email'>
					{dict.users.modalAdd.email}
				</label>
				<Controller
					render={ ({ field }) => <TextField
						size='small'
						fullWidth
						placeholder={ dict.users.modalAdd.placeholderEmail }
						inputProps={ { maxLength: 256 } }
						{ ...field }
					/> }
					name='email'
					control={ control }
					errors={ errors.email }
				/>
				<p className="modal-children__error">{errors && errors.email && errors.email.message}</p>
				<div className="modal-children__add-user-input-info">
					<i className="icon-circle-info" />
					<p>{dict.users.modalAdd.emailInfo}</p>
				</div>

				{fields.map((field, index) => (
					<div
						className="modal-children__departments-input-wrapper"
						key={ field.id }
					>
						<label
							className="modal-children__label"
							htmlFor={ `departments-${field.id}` }>
							{dict.users.modalAdd.departmentLabel} 
							{mode !== 'standalone' && <>
								&nbsp;{index + 1}
							</>}
						</label>
						<Controller
							name={ `departments[${index}].name` }
							control={ control }
							render={ () => (
								<LettersDeptAutoSuggest
									placeholder={ dict.users.modalAdd.departmentPlaceholder }
									onChangeCallback={ (value) => { reset("role", {keepValues: true, keepDefaultValues: false}); setValue(`departments[${index}].name`, value); } }
									param="label"
									formInput={ true }
									startDepartmentId={ fields[index]?.departmentId || null }
									controlURL={ false }
								/>
							) }
						/>
						{index === 0 &&
							(<div
								className="modal-children__add-user-input-info modal-children__add-user-input-info--departments">
								<i className="icon-circle-info" />
								<p>
									{dict.users.modalAdd.departmentInfo}
								</p>
							</div>)
						}
						{(index > 0 || (index === 0 && watchFirstDepartmentsField !== '')) && (
							<button
								className={ `modal-children__add-user-departments-btn modal-children__add-user-remove-departments-btn ${index === 0 ? "modal-children__add-user-remove-departments-btn--first" : ""} button__std` }
								type="button"
								onClick={ () => {
									if (fields.length - 1 === 0) setValue("departments", [ { name: "" } ], { shouldDirty: true });
									else remove(index);
								} }>
								<img
									src={ TrashIcon }
									alt="remove-department"
								/>
								<span>{dict.users.modalAdd.removeDepartmentInfo}</span>
							</button>
						)}
					</div>
				))}
				{mode !== 'standalone' && watchFirstDepartmentsField?.trim() !== "" && (
					<button
						type="button"
						className="modal-children__add-user-departments-btn button__std"
						onClick={ () => append({ name: "" }) }
					>
						<img
							src={ PlusIcon }
							alt="remove-department"
						/>
						{dict.users.modalAdd.addNewDepartmentInfo}
					</button>
				)}

				<label
					className="modal-children__label"
					htmlFor="role">
					{dict.users.modalAdd.role}
				</label>
				<Controller
					render={ ({ field }) => <Select
						size='small'
						fullWidth
						isSearchable={ false }
						placeholder={ dict.users.modalAdd.placeholderRole }
						options={ roles }
						className="modal-children__add-user-select"
						components={ {
							IndicatorSeparator: () => null,
							DropdownIndicator: (props) => (
								<components.DropdownIndicator { ...props }>
									<div className="icon-arrow-down-full arrow" />
								</components.DropdownIndicator>
							),
						} }
						{ ...field }
					/> }
					name="role"
					control={ control }
				/>
				<p className="modal-children__error">{errors && errors.role && errors.role.message}</p>
			</form>
		</ModalWrapperChildren>
	);
};

export default ModalAddUser;
